import React, { useRef, useEffect } from 'react';
import { Copy, Clock, ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';
import DropdownCell from './cells/DropdownCell';
import InputCell from './cells/InputCell';

const NamingTable = ({
  displayedRows,
  staticDropdownData,
  selectedRowId,
  highlightedRowId,
  onUpdateRow,
  onDuplicateRow,
  onSetSelectedRowId,
  onClearHighlight,
  onCopyFileName,
  sortConfig,
  onSort,
  onToggleGroup
}) => {

  // Refs for main scroll container and table
  const scrollContainerRef = useRef(null);
  const tableRef = useRef(null);
  const fixedScrollbarRef = useRef(null);

  // Keep a fixed horizontal scrollbar at bottom of viewport synced with table
  useEffect(() => {
    const scroller = scrollContainerRef.current;
    const fixedBar = fixedScrollbarRef.current;
    if (!scroller || !fixedBar) return;

    // The inner spacer div that defines scrollable width for the fixed bar
    const spacer = fixedBar.firstChild;

    const syncFromFixed = () => {
      if (scroller.scrollLeft !== fixedBar.scrollLeft) {
        scroller.scrollLeft = fixedBar.scrollLeft;
      }
    };
    const syncFromMain = () => {
      if (fixedBar.scrollLeft !== scroller.scrollLeft) {
        fixedBar.scrollLeft = scroller.scrollLeft;
      }
    };

    fixedBar.addEventListener('scroll', syncFromFixed, { passive: true });
    scroller.addEventListener('scroll', syncFromMain, { passive: true });

    const updateSpacerWidth = () => {
      if (tableRef.current && spacer) {
        // Add a small buffer (1px) to ensure scrollbar reaches end cleanly
        spacer.style.width = tableRef.current.scrollWidth + 1 + 'px';
      }
    };
    updateSpacerWidth();

    // Resize observer for dynamic column width changes
    let resizeObserver;
    if (window.ResizeObserver && tableRef.current) {
      resizeObserver = new ResizeObserver(updateSpacerWidth);
      resizeObserver.observe(tableRef.current);
    }
    window.addEventListener('resize', updateSpacerWidth);

    return () => {
      fixedBar.removeEventListener('scroll', syncFromFixed);
      scroller.removeEventListener('scroll', syncFromMain);
      window.removeEventListener('resize', updateSpacerWidth);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [displayedRows]);

  return (
    <>
      <main className="max-w-full mx-auto px-8 py-8 pb-14" role="main">
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-200/50 overflow-hidden">
          <div ref={scrollContainerRef} className="overflow-x-auto" role="region" aria-label="File naming records table" tabIndex="0">
            <table ref={tableRef} id="main-table" className="w-full" role="table" aria-label="File naming records with actions and details">
            <caption className="sr-only">
              File naming records table showing {displayedRows.length} records. 
              Each row contains file information including code, devices, client, page, section, and other metadata.
              Required fields are marked with asterisk.
            </caption>
            <thead className="bg-gradient-to-r from-gray-100 to-gray-200 border-b border-gray-300">
              <tr>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide w-20">Actions</th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide w-24">Code</th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-36">
                  Device(s) <span className="text-red-700" aria-label="required">*</span>
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-36">
                  Client <span className="text-red-700" aria-label="required">*</span>
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-36">
                  Page/Menu <span className="text-red-700" aria-label="required">*</span>
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-36">
                  Section
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-32">First</th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-32">Last</th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-36">Usage</th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-44">Description</th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-32 w-32">Width</th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-32 w-32">Height</th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-36">
                  Date <span className="text-red-700" aria-label="required">*</span>
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-36">
                  Employee <span className="text-red-700" aria-label="required">*</span>
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-72">File Name</th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-44">Drive Link</th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-44">
                  <button
                    onClick={() => onSort('created')}
                    className="flex items-center space-x-1 hover:text-blue-700 focus:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded px-1 transition-colors duration-200"
                    aria-label={`Sort by Created column ${sortConfig.key === 'created' ? (sortConfig.direction === 'asc' ? '(currently ascending)' : '(currently descending)') : ''}`}
                  >
                    <span>Created</span>
                    {sortConfig.key === 'created' && (
                      sortConfig.direction === 'asc' ? (
                        <ChevronUp className="w-3 h-3" aria-hidden="true" />
                      ) : (
                        <ChevronDown className="w-3 h-3" aria-hidden="true" />
                      )
                    )}
                  </button>
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide min-w-44">Updated</th>
              </tr>
            </thead>
            <tbody className="bg-white/50 divide-y divide-gray-200/40">
              {displayedRows.map(row => (
                row.__group ? (
                  <tr
                    key={row.id}
                    // Changed group header color scheme from green to blue to align with page styling.
                    className={`transition-colors duration-200 ${row.groupLevel === 1 ? 'bg-blue-50' : 'bg-blue-50/30'} group`}
                    role="row"
                  >
                    <td colSpan={19} className={`px-4 py-2 text-[10px] font-semibold tracking-wide rounded ${row.groupLevel === 1 ? 'text-blue-800 border-l-4 border-blue-400 bg-gradient-to-r from-blue-100 to-blue-50' : 'text-blue-700 border-l-2 border-blue-300 bg-gradient-to-r from-blue-50 to-blue-50'} font-mono flex items-center space-x-2`}> 
                      <button
                        type="button"
                        onClick={() => row.groupKey && onToggleGroup(row.groupKey)}
                        className={`p-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 ${row.collapsed ? 'hover:bg-blue-200' : 'hover:bg-blue-100'}`}
                        aria-label={`${row.collapsed ? 'Expand' : 'Collapse'} group ${row.groupLabel}`}
                        aria-expanded={!row.collapsed}
                      >
                        {row.collapsed ? <ChevronRight className="w-3 h-3" aria-hidden="true" /> : <ChevronDown className="w-3 h-3" aria-hidden="true" />}
                      </button>
                      <span>{row.groupLabel}</span>
                    </td>
                  </tr>
                ) : (
                <tr 
                  key={row.id} 
                  className={`hover:bg-blue-50/50 focus-within:bg-blue-50/50 transition-colors duration-200 ${
                    highlightedRowId === row.id ? 'bg-blue-100/80 border-l-4 border-blue-400' : ''
                  }`}
                  role="row"
                  onClick={() => {
                    // Clear highlight when clicking anywhere on a different row
                    if (highlightedRowId && highlightedRowId !== row.id) {
                      onClearHighlight();
                    }
                  }}
                >
                  {/* Actions */}
                  <td className="px-2 py-3">
                    <div className="flex space-x-2" role="group" aria-label={`Actions for row ${row.code}`}>
                      <button
                        onClick={() => onDuplicateRow(row)}
                        className="p-2 text-gray-600 hover:text-blue-700 hover:bg-blue-100 focus:text-blue-700 focus:bg-blue-100 focus:outline-none focus:ring-4 focus:ring-blue-200 rounded-lg transition-all duration-200"
                        aria-label={`Duplicate row ${row.code}`}
                      >
                        <Copy className="w-4 h-4" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => onSetSelectedRowId(row.id)}
                        className={`p-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-4 ${
                          selectedRowId === row.id 
                            ? 'text-purple-700 bg-purple-100 focus:ring-purple-200' 
                            : 'text-gray-600 hover:text-purple-700 hover:bg-purple-100 focus:text-purple-700 focus:bg-purple-100 focus:ring-purple-200'
                        }`}
                        aria-label={`${selectedRowId === row.id ? 'Hide' : 'Show'} activity log for row ${row.code}`}
                        aria-pressed={selectedRowId === row.id}
                      >
                        <Clock className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                  
                  {/* CODE */}
                  <td className="px-2 py-3">
                    <div className="text-xs font-bold text-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-2 rounded-lg border">
                      {row.code}
                    </div>
                  </td>
                  
                  {/* DEVICE(S) */}
                  <td className="px-2 py-3">
                    <DropdownCell
                      value={row.device}
                      // Ensure previously saved value still appears even if not in the static list
                      options={(function() { 
                        const base = staticDropdownData.devices || []; 
                        return row.device && !base.includes(row.device) ? [row.device, ...base] : base; 
                      })()}
                      onChange={(value) => onUpdateRow(row.id, 'device', value)}
                      required
                      fieldName="Device"
                      rowId={row.id}
                    />
                  </td>
                  
                  {/* CLIENT */}
                  <td className="px-2 py-3">
                    <DropdownCell
                      value={row.client}
                      options={staticDropdownData.clients}
                      onChange={(value) => onUpdateRow(row.id, 'client', value)}
                      required
                      fieldName="Client"
                      rowId={row.id}
                    />
                  </td>
                  
                  {/* PAGE/MENU */}
                  <td className="px-2 py-3">
                    <DropdownCell
                      value={row.page}
                      options={staticDropdownData.pages}
                      onChange={(value) => onUpdateRow(row.id, 'page', value)}
                      required
                      fieldName="Page/Menu"
                      rowId={row.id}
                    />
                  </td>
                  
                  {/* SECTION */}
                  <td className="px-2 py-3">
                    <DropdownCell
                      value={row.section}
                      options={staticDropdownData.sections}
                      onChange={(value) => onUpdateRow(row.id, 'section', value)}
                      fieldName="Section"
                      rowId={row.id}
                    />
                  </td>
                  
                  {/* FIRST */}
                  <td className="px-2 py-3">
                    <InputCell
                      value={row.first}
                      onChange={(value) => onUpdateRow(row.id, 'first', value)}
                      placeholder="First name"
                      fieldName="First"
                      rowId={row.id}
                      validationError={row.validationErrors?.first}
                    />
                  </td>
                  
                  {/* LAST */}
                  <td className="px-2 py-3">
                    <InputCell
                      value={row.last}
                      onChange={(value) => onUpdateRow(row.id, 'last', value)}
                      placeholder="Last name"
                      fieldName="Last"
                      rowId={row.id}
                      validationError={row.validationErrors?.last}
                    />
                  </td>
                  
                  {/* USAGE */}
                  <td className="px-2 py-3">
                    <DropdownCell
                      value={row.usage}
                      options={staticDropdownData.usage}
                      onChange={(value) => onUpdateRow(row.id, 'usage', value)}
                      fieldName="Usage"
                      rowId={row.id}
                    />
                  </td>
                  
                  {/* DESCRIPTION */}
                  <td className="px-2 py-3">
                    <InputCell
                      value={row.description}
                      onChange={(value) => onUpdateRow(row.id, 'description', value)}
                      placeholder="Describe the asset"
                      fieldName="Description"
                      rowId={row.id}
                      validationError={row.validationErrors?.description}
                    />
                  </td>
                  
                  {/* WIDTH */}
                  <td className="px-2 py-3">
                    <InputCell
                      value={row.width}
                      onChange={(value) => onUpdateRow(row.id, 'width', value)}
                      placeholder="Width (up to 5 digits)"
                      fieldName="Width"
                      rowId={row.id}
                      validationError={row.validationErrors?.width}
                      inputMode="numeric"
                      maxLength={5}
                    />
                  </td>
                  
                  {/* HEIGHT */}
                  <td className="px-2 py-3">
                    <InputCell
                      value={row.height}
                      onChange={(value) => onUpdateRow(row.id, 'height', value)}
                      placeholder="Height (up to 5 digits)"
                      fieldName="Height"
                      rowId={row.id}
                      validationError={row.validationErrors?.height}
                      inputMode="numeric"
                      maxLength={5}
                    />
                  </td>
                  
                  {/* DATE */}
                  <td className="px-2 py-3">
                    <InputCell
                      value={row.date}
                      onChange={(value) => onUpdateRow(row.id, 'date', value)}
                      type="date"
                      fieldName="Date"
                      rowId={row.id}
                      validationError={row.validationErrors?.date}
                    />
                  </td>
                  
                  {/* EMPLOYEE */}
                  <td className="px-2 py-3">
                    <DropdownCell
                      value={row.employee}
                      options={staticDropdownData.employees}
                      onChange={(value) => onUpdateRow(row.id, 'employee', value)}
                      required
                      fieldName="Employee"
                      rowId={row.id}
                    />
                  </td>
                  
                  {/* FILE NAME */}
                  <td className="px-2 py-3">
                    <button 
                      className="w-full text-left text-xs font-mono text-gray-800 bg-gradient-to-r from-blue-50 to-purple-50 px-3 py-2 rounded-lg border-2 border-blue-200/50 break-all cursor-pointer hover:bg-gradient-to-r hover:from-blue-100 hover:to-purple-100 focus:bg-gradient-to-r focus:from-blue-100 focus:to-purple-100 focus:outline-none focus:ring-4 focus:ring-blue-200 transition-all duration-200 relative group"
                      onClick={() => onCopyFileName(row.fileName)}
                      aria-label={`Copy filename to clipboard: ${row.fileName || 'Auto-generated filename'}`}
                    >
                      {row.fileName || 'Auto-generated filename'}
                      <span className="absolute bottom-1 right-2 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-200 text-[9px] font-medium text-gray-700 bg-white/90 px-1 py-0.5 rounded shadow-sm" aria-hidden="true">
                        Click to copy
                      </span>
                    </button>
                  </td>
                  
                  {/* DRIVE LINK */}
                  <td className="px-2 py-3">
                    <InputCell
                      value={row.driveLink}
                      onChange={(value) => onUpdateRow(row.id, 'driveLink', value)}
                      placeholder="Drive Link URL"
                      fieldName="Drive Link"
                      rowId={row.id}
                    />
                  </td>
                  
                  {/* CREATED */}
                  <td className="px-2 py-3">
                    <div className="text-[10px] font-medium text-gray-600 bg-gray-100 px-3 py-2 rounded-lg" title={row.created}>
                      {row.created ? new Date(row.created).toLocaleString() : ''}
                    </div>
                  </td>
                  
                  {/* UPDATED */}
                  <td className="px-2 py-3">
                    <div className="text-[10px] font-medium text-gray-600 bg-gray-100 px-3 py-2 rounded-lg" title={row.updated}>
                      {row.updated ? new Date(row.updated).toLocaleString() : ''}
                    </div>
                  </td>
                </tr>
                )
              ))}
            </tbody>
            </table>
          </div>
        </div>
      </main>
      {/* Fixed horizontal scrollbar (synced) */}
      <div
        ref={fixedScrollbarRef}
        className="fixed bottom-0 left-0 w-full h-5 overflow-x-auto overflow-y-hidden z-50 bg-gradient-to-r from-white/70 to-white/60 backdrop-blur supports-[backdrop-filter]:backdrop-blur-sm border-t border-gray-300 shadow-inner"
        aria-label="Horizontal scroll for file naming table"
        role="presentation"
      >
        {/* Spacer element whose width mirrors the table scrollable width. Height minimal to show scrollbar only. */}
        <div className="h-px" />
      </div>
    </>
  );
};

export default NamingTable;